/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { FormattedMessage } from 'react-intl'
import { clamp } from 'lodash'

import type { ClusterInstanceInfo } from '@modules/cloud-api/v1/types'

import StatPressureIndicator from '../../../StatPressureIndicator'

import type { FunctionComponent } from 'react'

type Props = {
  instance: ClusterInstanceInfo
  label?: JSX.Element
  ratio?: number
}

const JvmMemoryPressure: FunctionComponent<Props> = ({ instance, label, ratio = 1 }) => {
  const memoryPressure = getMemoryPressure({ instance, ratio })
  const memoryCapacity = instance.memory?.instance_capacity ?? 0
  const stressed = isUnderPressure(memoryPressure)

  const defaultLabel = (
    <FormattedMessage id='jvm-memory-pressure.default-label' defaultMessage='JVM memory pressure' />
  )

  return (
    <StatPressureIndicator
      label={label || defaultLabel}
      pressure={memoryPressure}
      total={memoryCapacity}
      hideTotal={true}
      isStressed={stressed}
    />
  )
}

function getMemoryPressure({
  instance,
  ratio,
}: {
  instance: ClusterInstanceInfo
  ratio: number
}): number {
  const memoryPressure = instance.memory?.memory_pressure ?? 0

  return clamp(memoryPressure / ratio, 0, 100)
}

function isUnderPressure(memoryPressure: number) {
  return memoryPressure >= 75
}

export default JvmMemoryPressure
